import type { RouteLocation, RouteLocationNormalized, RouteParamsGeneric } from 'vue-router'
import { RoutingDefinitions } from '@generator/types/RoutingDefinitions'
import { PortalType } from '@studyportals/domain-client'
import { getSearchRouteResolver } from '@app/routing/resolver/searchRouteRegistry'
import type { ResolvedSearchRoute } from '@app/routing/resolver/SearchRouteResolver'

type SearchRouteLocation = Pick<RouteLocation, 'path' | 'meta'> & { params?: RouteParamsGeneric }

/**
 * Resolves the generated search route (name, filter params, filter types) behind one of the search
 * catch-all routes. Returns null for any other route, or when the path doesn't match a search route.
 */
export const getResolvedSearchRoute = (route: SearchRouteLocation): ResolvedSearchRoute | null => {
	const searchRouteType = route.meta?.searchRouteType
	if (!searchRouteType || typeof route.path !== 'string') return null

	return getSearchRouteResolver(searchRouteType)?.match(route.path) ?? null
}

/**
 * Route params including the filter params (`lv`, `di`, `ci`, ...) of the resolved search route.
 * Use this instead of `route.params` to read filter params.
 */
export const getSearchRouteParams = (route: SearchRouteLocation): RouteParamsGeneric => {
	const params = route.params ?? {}
	const resolved = getResolvedSearchRoute(route)

	return resolved ? { ...params, ...resolved.params } : params
}

export const isCatchAllRoute = (to: RouteLocationNormalized): boolean => {
	if (to.matched.length === 0) return true;

	if (to.meta?.searchRouteType && !getResolvedSearchRoute(to)) return true;

	return to.matched.some((record) => {
		return record.name === RoutingDefinitions.CATCH_ALL;
	});
};

export const DEFAULT_PORTAL_TYPE = PortalType.MASTER;
